/*
 * Copyright (C) 2026 Bohemian Miser <https://substack.com/@bohemianmiser>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { RecipeNode, IconStats, IconIndexEntry, ShortlistEntry, SearchTerm, RecipeGraph, IconStyleId, RecipePatch } from './types';
import { standardizeIngredientName } from '../utils';

/**
 * Returns the canonical ingredient name for a node — used for icon lookup and standardisation.
 * Prefers visualDescription over text (visualDescription is the icon-specific description).
 */
export function getNodeIngredientName(node: RecipeNode): string {
    return node.visualDescription || node.text;
}

/**
 * Builds an ingredient node's display `text` from its structured parts, e.g.
 * `(2, 'cup', 'Flour') -> '2 cup Flour'`. Collapses the gaps left by a missing
 * unit. Single source of truth for the label used by serves-scaling and the
 * ingredient quantity editor.
 */
export function buildIngredientText(
    quantity: number,
    unit: string | undefined,
    canonicalName: string,
): string {
    return `${quantity} ${unit || ''} ${canonicalName}`.trim().replace(/\s+/g, ' ');
}

/**
 * Computes the field patch for one ingredient row's structured editor (the
 * number / unit / name boxes in the ingredients sidebar). `draft.qty` is the
 * amount as shown to the user (scaled to the current serves); it is stored
 * unscaled as `quantity`. `unit` and `name` map to `unit` / `canonicalName`,
 * and `text` (the diagram label) is rebuilt from all three so the number, unit
 * and name are not duplicated across separate fields.
 *
 * Returns only the fields that actually changed (empty object = no-op, so the
 * caller can skip a spurious undo entry).
 */
export function computeIngredientRowPatch(
    node: RecipeNode,
    draft: { qty: string; unit: string; name: string },
    scale: number,
): Partial<RecipeNode> {
    const seedQty = node.quantity != null ? String(Math.round(node.quantity * scale * 100) / 100) : '';
    const qtyStr = draft.qty.trim();
    const unit = draft.unit.trim();
    const name = draft.name.trim();
    const seedName = (node.canonicalName ?? node.text ?? '').trim();

    const qtyChanged = qtyStr !== seedQty;
    const unitChanged = unit !== (node.unit ?? '').trim();
    const nameChanged = name !== seedName;

    const patch: Partial<RecipeNode> = {};
    if (!qtyChanged && !unitChanged && !nameChanged) return patch;

    if (qtyChanged) {
        if (qtyStr === '') {
            patch.quantity = undefined;
        } else {
            const displayed = parseFloat(qtyStr);
            if (Number.isFinite(displayed)) {
                patch.quantity = scale ? Math.round((displayed / scale) * 100) / 100 : displayed;
            }
        }
    }
    if (unitChanged) patch.unit = unit;
    if (nameChanged) patch.canonicalName = name;

    // Rebuild the display label from the (scaled) number + unit + name — the
    // single source of truth, so nothing is duplicated across fields.
    const newText = [qtyStr, unit, name].filter(s => s !== '').join(' ').replace(/\s+/g, ' ').trim();
    if (newText !== '' && newText !== (node.text ?? '')) patch.text = newText;

    return patch;
}

/** Returns the display theme for a node's icon. */
export function getNodeTheme(node: RecipeNode): IconStyleId {
    return (node.iconTheme as IconStyleId) || 'classic';
}

/**
 * Returns the HyDE queries for a node, or an empty array when none are present.
 */
export function getNodeHydeQueries(node: RecipeNode): string[] {
    return node.hydeQueries || [];
}

/**
 * Extracts a unique list of batch ingredients (with their aggregated HyDE queries)
 * from a given list of nodes. Nodes without visual descriptions are ignored.
 */
export function extractBatchIngredients(nodes: RecipeNode[]): { name: string; queries: string[] }[] {
    const hydeMap = new Map<string, string[]>();
    for (const node of nodes) {
        if (!node.visualDescription) continue;
        const stdName = standardizeIngredientName(getNodeIngredientName(node));
        const queries = getNodeHydeQueries(node);
        const existing = hydeMap.get(stdName) ?? [];
        hydeMap.set(stdName, Array.from(new Set([...existing, ...queries])));
    }
    return Array.from(hydeMap.entries()).map(([name, queries]) => ({
        name,
        queries: queries.length ? queries : [name],
    }));
}

/** Derives the Storage path for an icon from its ID and ingredient name. */
export function getIconPath(iconId: string, ingredientName: string): string {
    const shortId = iconId.substring(0, 8);
    const kebabName = ingredientName.trim().replace(/\s+/g, '-');
    return `icons/${kebabName}-${shortId}.png`;
}

/** Derives the thumb Storage path. */
export function getIconThumbPath(iconId: string, ingredientName: string): string {
    return getIconPath(iconId, ingredientName).replace('.png', '.thumb.png');
}

export function getNodeIcon(node: RecipeNode): IconStats | undefined {
    const entry = getCurrentEntry(node);
    return entry ? getEntryIcon(entry) : undefined;
}

/** @deprecated - icon writes go through the shortlist; this is a no-op. */
export function setNodeIcon(node: RecipeNode, _icon: IconStats) {
    return node;
}

/**
 * Assigns a shortlist (and optionally its index) onto a node in place.
 * When `index` is omitted the node's existing shortlistIndex is left untouched.
 */
export function assignNodeShortlist(node: RecipeNode, entries: ShortlistEntry[], index?: number): void {
    node.iconShortlist = entries;
    if (index !== undefined) {
        node.shortlistIndex = index;
        // An explicit index means the shortlist CONTENTS changed (search
        // apply, forge prepend) — the new list has not been fully shown or
        // cycled, so the seen-everything flags must not leak onto entries the
        // user never saw. The index-less call (saveRecipe persisting
        // impression flags back) leaves them alone.
        node.shortlistCycled = undefined;
        node.shortlistSeenAll = undefined;
    }
}

/** Clears all shortlist state from the node (shortlist, index, and seen flags). */
export function clearNodeShortlist(node: RecipeNode): void {
    node.iconShortlist = undefined;
    node.shortlistIndex = undefined;
    node.shortlistCycled = undefined;
    node.shortlistSeenAll = undefined;
}

/**
 * Restores `serverNode`'s shortlist state (iconShortlist + shortlistIndex +
 * shortlistCycled) onto `node` IN PLACE — but ONLY when `node` currently has
 * no shortlist and `serverNode` has one. No-op otherwise.
 *
 * Used by saveRecipe's server-state reconciliation (issue #221): a stale
 * client save whose node lost its shortlist must not erase the server's
 * copy. When the client node DOES have a shortlist, this leaves it (and its
 * index/cycled flag) completely alone — that path is owned by the client's
 * own save data plus the impression/rejection delta logic above, not this
 * restore.
 */
export function restoreNodeShortlistFromServer(node: RecipeNode, serverNode: RecipeNode): void {
    if (getNodeShortlistLength(node) > 0) return;
    if (getNodeShortlistLength(serverNode) === 0) return;
    node.iconShortlist = serverNode.iconShortlist;
    node.shortlistIndex = serverNode.shortlistIndex;
    node.shortlistCycled = serverNode.shortlistCycled;
    node.shortlistSeenAll = serverNode.shortlistSeenAll;
}

/**
 * Returns all ShortlistEntries the user has seen in the current cycle:
 * - If shortlistCycled is true: all entries in the shortlist
 * - Otherwise: entries 0 through shortlistIndex inclusive
 */
export function getSeenEntries(node: RecipeNode): ShortlistEntry[] {
    const shortlist = node.iconShortlist;
    if (!shortlist || shortlist.length === 0) return [];
    const upTo = node.shortlistCycled ? shortlist.length : (node.shortlistIndex ?? 0) + 1;
    return shortlist.slice(0, upTo);
}

/**
 * Returns the IDs of every icon the user has seen in the current cycle.
 */
export function getSeenIconIds(node: RecipeNode): string[] {
    return getSeenEntries(node).map(e => getEntryIcon(e).id);
}

/**
 * Returns icon IDs for seen entries that have not yet had an impression recorded.
 */
// this might be not needed.
export function getPendingImpressionIds(node: RecipeNode): string[] {
    return getSeenEntries(node)
        .filter(e => !getEntryHasImpressed(e))
        .map(e => getEntryIcon(e).id);
}

/**
 * Returns icon IDs for seen entries that have not yet had a rejection recorded.
 */
// this might be not needed.
export function getPendingRejectionIds(node: RecipeNode): string[] {
    return getSeenEntries(node)
        .filter(e => !getEntryHasRejected(e))
        .map(e => getEntryIcon(e).id);
}

/**
 * Like getPendingImpressionIds but returns {id, ingredientId} pairs.
 * ingredientId is standardized from the icon's own visualDescription so that
 * search icons (which may belong to a different ingredient than the node) are
 * looked up in the correct `ingredients_new` document.
 */
export function getPendingImpressionTargets(node: RecipeNode): { id: string; ingredientId: string }[] {
    const fallback = standardizeIngredientName(getNodeIngredientName(node));
    return getSeenEntries(node)
        .filter(e => !getEntryHasImpressed(e))
        .map(e => {
            const icon = getEntryIcon(e);
            return { id: icon.id, ingredientId: icon.visualDescription ? standardizeIngredientName(icon.visualDescription) : fallback };
        });
}

/**
 * Like getPendingRejectionIds but returns {id, ingredientId} pairs.
 * ingredientId is standardized from the icon's own visualDescription so that
 * search icons (which may belong to a different ingredient than the node) are
 * looked up in the correct `ingredients_new` document.
 */
export function getPendingRejectionTargets(node: RecipeNode): { id: string; ingredientId: string }[] {
    const fallback = standardizeIngredientName(getNodeIngredientName(node));
    return getSeenEntries(node)
        .filter(e => !getEntryHasRejected(e))
        .map(e => {
            const icon = getEntryIcon(e);
            return { id: icon.id, ingredientId: icon.visualDescription ? standardizeIngredientName(icon.visualDescription) : fallback };
        });
}

/**
 * Returns a new shortlist with hasImpressed=true for all seen entries that weren't
 * already marked. Entries beyond the seen range are returned unchanged.
 */
export function markSeenEntriesImpressed(node: RecipeNode) {
    if (!node.iconShortlist) return;
    const upTo = node.shortlistCycled ? node.iconShortlist.length : (node.shortlistIndex ?? 0) + 1;
    node.iconShortlist = node.iconShortlist.map((e, i) =>
        i < upTo && !getEntryHasImpressed(e) ? { ...e, hasImpressed: true } : e
    );
}

/**
 * Returns a new shortlist with hasImpressed=true on the entry at `index`.
 * Used when a shortlist is first assigned and we immediately record an impression
 * for the entry shown at position 0.
 */
export function markEntryImpressedAtIndex(entries: ShortlistEntry[], index: number): ShortlistEntry[] {
    return entries.map((e, i) => i === index && !getEntryHasImpressed(e) ? { ...e, hasImpressed: true } : e);
}

/**
 * Marks seen entries in the given node.
 */
export function markSeenEntriesRejected(node: RecipeNode) {
    if (!node.iconShortlist) return;
    const upTo = node.shortlistCycled ? node.iconShortlist.length : (node.shortlistIndex ?? 0) + 1;
    node.iconShortlist = node.iconShortlist.map((e, i) =>
        i < upTo && !getEntryHasRejected(e) ? { ...e, hasRejected: true } : e
    );
}

export interface ShortlistDeltaEntry {
    id: string;
    ingredientId: string;
}

export interface ShortlistDelta {
    toImpres: ShortlistDeltaEntry[];
    toReject: ShortlistDeltaEntry[];
    /** Icons that were previously recorded as rejected but are now selected (cycled back). */
    toUnreject: ShortlistDeltaEntry[];
    updatedShortlist: ShortlistEntry[];
}

/**
 * Computes the delta between a previously-saved node (from Firestore) and the
 * incoming client node. The backend is authoritative for what icons exist; only
 * `shortlistIndex` and `shortlistCycled` are trusted from the client.
 *
 * Algorithm: for each icon in the new shortlist, compute whether it *should* be
 * impressed/rejected given the new index, then subtract the already-recorded state
 * (old flags, matched by icon ID so forge/prepend doesn't break matching).
 *
 *   impressionDelta = int(shouldBeImpressed) - int(wasImpressed)   → only 0 or +1
 *   rejectionDelta  = int(shouldBeRejected)  - int(wasRejected)    → -1, 0, or +1
 *
 * Icons in old-but-not-new (removed by forge dedup) are ignored — no delta.
 * Impressions are monotonic: once seen, always seen; cycling back never un-impresses.
 * Rejections track the currently-selected icon: only the selected entry can un-reject.
 */

/**
 * Stamps hasImpressed/hasRejected on each entry based purely on shortlistIndex
 * and shortlistCycled, so the delta loop can read flags from the entry directly
 * without caring about index arithmetic.
 */
/**
 * Stamps hasImpressed/hasRejected on each entry based purely on shortlistIndex
 * and shortlistCycled. Unseen entries (beyond the current index) are returned
 * unchanged so their existing flags are preserved — cycling back should not
 * inadvertently clear rejections on icons the user hasn't reached yet.
 */
function stampShortlistFlags(shortlist: ShortlistEntry[], shortlistIndex: number, shortlistCycled?: boolean, shortlistSeenAll?: boolean): ShortlistEntry[] {
    const upTo = shortlistCycled ? shortlist.length : shortlistIndex + 1;
    return shortlist.map((e, i) => {
        if (i >= upTo) {
            // Not cycled/selected past. Shown all at once in the icon editor
            // modal → impression only; otherwise unseen: preserve as-is.
            return shortlistSeenAll && !getEntryHasImpressed(e) ? { ...e, hasImpressed: true } : e;
        }
        return {
            ...e,
            hasImpressed: true,
            hasRejected:  i !== shortlistIndex ? true : undefined,
        };
    });
}

export function computeShortlistDelta(
    oldNode: RecipeNode | null | undefined,
    newNode: RecipeNode,
): ShortlistDelta {
    const rawShortlist = newNode.iconShortlist;
    const empty: ShortlistDelta = { toImpres: [], toReject: [], toUnreject: [], updatedShortlist: rawShortlist ?? [] };
    if (!rawShortlist || rawShortlist.length === 0) return empty;

    const newShortlist = stampShortlistFlags(rawShortlist, newNode.shortlistIndex ?? 0, newNode.shortlistCycled, newNode.shortlistSeenAll);

    const ingId = (icon: IconStats) =>
        icon.visualDescription ? standardizeIngredientName(icon.visualDescription) : "WTF";


    // Match old flags by icon ID so prepend/forge doesn't break flag matching.
    const oldFlagsByIconId = new Map<string, ShortlistEntry>();
    for (const e of oldNode?.iconShortlist ?? []) {
        oldFlagsByIconId.set(getEntryID(e), e);
    }

    
    const toImpres: ShortlistDeltaEntry[] = [];
    const toReject: ShortlistDeltaEntry[] = [];
    const toUnreject: ShortlistDeltaEntry[] = [];
    const updatedShortlist: ShortlistEntry[] = [];

    for (const e of newShortlist) {

        const old = oldFlagsByIconId.get(getEntryID(e))
        const nR = getEntryHasRejected(e);
        const nI = getEntryHasImpressed(e);
        let oR = false;
        let oI = false;
        if (old !== undefined) {
            oR = getEntryHasRejected(old);
            oI = getEntryHasImpressed(old);
        }
        // New impression.
        if (nI && !oI) toImpres.push({ id: getEntryID(e), ingredientId: ingId(getEntryIcon(e)) });
        
        // Rejection changed.
        if (nR && !oR) toReject.push({ id: getEntryID(e), ingredientId: ingId(getEntryIcon(e)) });
        if (!nR && oR) toUnreject.push({ id: getEntryID(e), ingredientId: ingId(getEntryIcon(e)) });

        // Once Impressed, always impressed.
        // Rejection changes.
        updatedShortlist.push({
            ...e,
            hasImpressed: nI || oI,
            hasRejected:  nR,
        });
    }
    return { toImpres, toReject, toUnreject, updatedShortlist };
}
// export function computeShortlistDelta(
//     oldNode: RecipeNode | null | undefined,
//     newNode: RecipeNode,
// ): ShortlistDelta {
//     const newShortlist = newNode.iconShortlist;
//     const empty: ShortlistDelta = { toImpres: [], toReject: [], toUnreject: [], updatedShortlist: newShortlist ?? [] };
//     if (!newShortlist || newShortlist.length === 0) return empty;

//     const fallback = standardizeIngredientName(getNodeIngredientName(newNode));
//     const ingId = (icon: IconStats) =>
//         icon.visualDescription ? standardizeIngredientName(icon.visualDescription) : fallback;

//     const newIndex = newNode.shortlistIndex ?? 0;
//     const newCycled = newNode.shortlistCycled ?? false;
//     const upTo = newCycled ? newShortlist.length : newIndex + 1;

//     // Match old flags by icon ID so prepend/forge doesn't break flag matching.
//     const oldFlagsByIconId = new Map<string, ShortlistEntry>();
//     for (const e of oldNode?.iconShortlist ?? []) {
//         oldFlagsByIconId.set(getEntryIcon(e).id, e);
//     }

//     const toImpres: ShortlistDeltaEntry[] = [];
//     const toReject: ShortlistDeltaEntry[] = [];
//     const toUnreject: ShortlistDeltaEntry[] = [];
//     const updatedShortlist: ShortlistEntry[] = [];

//     for (let i = 0; i < newShortlist.length; i++) {
//         const entry = newShortlist[i];
//         const icon = getEntryIcon(entry);
//         const target: ShortlistDeltaEntry = { id: icon.id, ingredientId: ingId(icon) };

//         const isSeen = i < upTo;
//         const isSelected = i === newIndex;

//         const oldEntry = oldFlagsByIconId.get(icon.id);
//         const wasImpressed = oldEntry ? getEntryHasImpressed(oldEntry) : false;
//         const wasRejected  = oldEntry ? getEntryHasRejected(oldEntry)  : false;

//         // Impressions: monotonic — once seen, always seen.
//         const nowImpressed = isSeen || wasImpressed;
//         const impressionDelta = (nowImpressed ? 1 : 0) - (wasImpressed ? 1 : 0);
//         if (impressionDelta > 0) toImpres.push(target);

//         // Rejections: only authoritative for seen entries. Unseen entries preserve old flag.
//         const nowRejected = isSeen ? (isSeen && !isSelected) : wasRejected;
//         const rejectionDelta = (nowRejected ? 1 : 0) - (wasRejected ? 1 : 0);
//         if (rejectionDelta > 0) toReject.push(target);
//         if (rejectionDelta < 0) toUnreject.push(target);

//         updatedShortlist.push({
//             ...entry,
//             hasImpressed: nowImpressed || undefined,
//             hasRejected:  nowRejected  || undefined,
//         });
//     }

//     return { toImpres, toReject, toUnreject, updatedShortlist };
// }

/** Returns the IconStats at a given shortlist position, or undefined if out of bounds. */
export function getShortlistIconAt(node: RecipeNode, index: number): IconStats | undefined {
    const entry = node.iconShortlist?.[index];
    return entry ? getEntryIcon(entry) : undefined;
}

/** Returns the node's shortlist entries, or an empty array when none are present. */
export function getNodeShortlist(node: RecipeNode): ShortlistEntry[] {
    return node.iconShortlist ?? [];
}

/** Returns the number of entries in the node's shortlist. */
export function getNodeShortlistLength(node: RecipeNode): number {
    return node.iconShortlist?.length ?? 0;
}

/**
 * Returns a new nodes array where the node with `nodeId` has its
 * `shortlistIndex` advanced by one (wrapping at length). All other
 * node object references are preserved.
 */
export function cycleShortlistNodes(graph: RecipeGraph, nodeId: string): RecipeNode[] {
    return graph.nodes.map(n => {
        if (n.id !== nodeId) return n;
        const length = getNodeShortlistLength(n);
        if (length === 0) return n;
        const next = ((n.shortlistIndex ?? 0) + 1) % length;
        return { ...n, shortlistIndex: next };
    });
}

/**
 * Returns a new nodes array where the node with `nodeId` has its
 * `shortlistIndex` set to `index` (icon editor modal pick). Out-of-bounds
 * indices and unchanged picks are no-ops that keep every node reference.
 */
export function setShortlistIndexNodes(graph: RecipeGraph, nodeId: string, index: number): RecipeNode[] {
    return graph.nodes.map(n => {
        if (n.id !== nodeId) return n;
        if (!Number.isInteger(index) || index < 0 || index >= getNodeShortlistLength(n)) return n;
        if ((n.shortlistIndex ?? 0) === index) return n;
        return { ...n, shortlistIndex: index };
    });
}

/**
 * Returns a new nodes array where the node with `nodeId` is flagged as having
 * had its whole shortlist shown at once (icon editor modal opened). Save-time
 * stamping turns this into an impression for every entry — see
 * stampShortlistFlags. No-op (reference-preserving) when already flagged or
 * the shortlist is empty.
 */
export function markShortlistSeenAllNodes(graph: RecipeGraph, nodeId: string): RecipeNode[] {
    return graph.nodes.map(n => {
        if (n.id !== nodeId) return n;
        if (n.shortlistSeenAll || getNodeShortlistLength(n) === 0) return n;
        return { ...n, shortlistSeenAll: true };
    });
}

/**
 * Returns a stable key that changes whenever the shortlist contents change
 * (i.e. when a forge completes and a new shortlist is assigned).
 * Safe to use as a useEffect dependency.
 */
export function getNodeShortlistKey(node: RecipeNode): string {
    return node.iconShortlist?.map(e => getEntryIcon(e).id).join(',') ?? '';
}

/**
 * Returns the thumb URL for the shortlist entry at the given index, or undefined
 * when the shortlist is empty or the index is out of bounds.
 * Use this instead of getNodeIconUrl when the display index is driven externally
 * (e.g. from a Zustand store).
 */
export function getNodeIconUrlAt(node: RecipeNode, index: number): string | undefined {
    const icon = getShortlistIconAt(node, index);
    if (!icon?.id) return undefined;
    const vd = icon.visualDescription || getNodeIngredientName(node);
    return getIconThumbUrl({ ...icon, visualDescription: vd });
}

/**
 * Returns true when the shortlist entry at the given index was resolved via
 * search rather than generation.
 */
export function isIconSearchMatchedAt(node: RecipeNode, index: number): boolean {
    if (!node.iconShortlist) return false;
    const entry = node.iconShortlist[index];
    return entry ? getEntryMatchType(entry) === 'search' : false;
}

export function hasNodeIcon(node: RecipeNode): boolean {
    const entry = getCurrentEntry(node);
    if (!entry) return false;
    const icon = getEntryIcon(entry);
    return !!icon.id;
}

/**
 * Reconstructs the public Firebase Storage URL from a path.
 * In emulator environments (local-project-id), points to the Storage emulator.
 * Format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encoded-path}?alt=media
 */
export function getIconUrl(path: string): string {
    const bucket = process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || 'recipe-lanes.firebasestorage.app';
    const encodedPath = encodeURIComponent(path);
    const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || '';
    if (projectId === 'local-project-id' || projectId.startsWith('demo-')) {
        const emulatorHost = process.env.NEXT_PUBLIC_FIREBASE_STORAGE_EMULATOR_HOST || '127.0.0.1:9199';
        return `http://${emulatorHost}/v0/b/${bucket}/o/${encodedPath}?alt=media`;
    }
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
}

/** Maps an IconIndexEntry (Firestore doc) to an IconStats object. */
export function iconIndexEntryToStats(entry: IconIndexEntry): IconStats {
    return { id: entry.icon_id, visualDescription: entry.ingredient_name };
}

export function getIconStoragePaths(icon: IconStats): { main: string; thumb: string } {
    if (!icon.visualDescription) throw new Error(`getIconStoragePaths: icon ${icon.id} has no visualDescription`);
    const stdName = standardizeIngredientName(icon.visualDescription);
    return { main: getIconPath(icon.id, stdName), thumb: getIconThumbPath(icon.id, stdName) };
}

export function getIconPublicUrl(icon: IconStats): string {
    return getIconUrl(getIconStoragePaths(icon).main);
}

export function getIconThumbUrl(icon: IconStats): string {
    return getIconUrl(getIconStoragePaths(icon).thumb);
}

export function withSearchTerms(icon: IconStats, hydeQueries: string[]): IconStats {
    if (hydeQueries.length === 0) return icon;
    const searchTerms: SearchTerm[] = hydeQueries.map(text => ({
        text,
        source: 'hyde_from_img' as const,
        addedAt: Date.now(),
    }));
    return { ...icon, searchTerms };
}

export function getNodeIconUrl(node: RecipeNode): string | undefined {
    const entry = getCurrentEntry(node);
    const icon = entry ? getEntryIcon(entry) : undefined;
    if (!icon?.id) return undefined;
    // Prefer the icon's own visualDescription (set at index time) over the node's.
    const vd = icon.visualDescription || getNodeIngredientName(node);
    return getIconThumbUrl({ ...icon, visualDescription: vd });
}

export function getNodeIconId(node: RecipeNode): string | undefined {
    const entry = getCurrentEntry(node);
    return entry ? getEntryIcon(entry).id : undefined;
}

export function getNodeIconMetadata(node: RecipeNode) {
    const entry = getCurrentEntry(node);
    return entry ? getEntryIcon(entry).metadata : undefined;
}

export function getNodeIconStatus(node: RecipeNode) {
    return node.status;
}
export function setNodeIconStatus(node: RecipeNode, status: 'pending' | 'processing' | 'failed') {
    node.status = status;
}

/**
 * Returns the status for a node by id from a graph, or undefined when
 * the graph or node is missing.
 */
export function getNodeStatus(graph: RecipeGraph | null, nodeId: string): 'pending' | 'processing' | 'failed' | undefined {
    if (!graph) return undefined;
    const node = graph.nodes.find(n => n.id === nodeId);
    if (!node) return undefined;
    return getNodeIconStatus(node);
}

/**
 * Sets the status on the node with `nodeId` in `graph` when the current
 * status is not already `pending` or `processing`. Returns true when a
 * change was made.
 */
export function setNodeStatus(graph: RecipeGraph | null, nodeId: string, status: 'pending' | 'processing' | 'failed'): boolean {
    if (!graph) return false;
    const node = graph.nodes.find(n => n.id === nodeId);
    if (!node) return false;
    const cur = getNodeIconStatus(node);
    if (cur === status) return false;
    setNodeIconStatus(node, status);
    return true;
}

/**
 * Sets the icon status on all nodes whose standardized ingredient name matches
 * `stdName`. Returns true if any node was changed.
 */
export function setNodeStatusByIngredient(
    graph: RecipeGraph | null,
    stdName: string,
    status: 'pending' | 'processing' | 'failed',
): boolean {
    if (!graph) return false;
    let changed = false;
    for (const node of graph.nodes) {
        if (!node.visualDescription) continue;
        if (standardizeIngredientName(getNodeIngredientName(node)) !== stdName) continue;
        const cur = getNodeIconStatus(node);
        if (cur === status) continue;
        setNodeIconStatus(node, status);
        changed = true;
    }
    return changed;
}

/**
 * Returns a plain-object subset of IconStats safe to embed in recipe nodes.
 * Strips legacy fields (path, url, fullPrompt, created_at, etc.) and Firestore
 * Timestamp instances that break Next.js client↔server serialization.
 */
// it's now safe for all things. this kept out url but is'nt needed anymore.
export function toRecipeIcon(icon: IconStats): IconStats {
    // return icon;
    return {
        id: icon.id,
        visualDescription: icon.visualDescription,
        metadata: icon.metadata,
        // status: icon.status,
        ...(icon.score !== undefined && { score: icon.score }),
        ...(icon.impressions !== undefined && { impressions: icon.impressions }),
        ...(icon.rejections !== undefined && { rejections: icon.rejections }),
    };
}

//deprecated.
export function applyIconToNode(node: RecipeNode, icon: IconStats) {
    // Only propagate essential visual/reference data, avoiding stale stats
    setNodeIcon(node, toRecipeIcon(icon));
    return node;
}

/**
 * Returns the matchType of the node's current shortlist entry — 'generated',
 * 'search', or undefined when no shortlist entry is present or shortlistIndex
 * has not been explicitly set.
 */
export function getIconMatchType(node: RecipeNode): 'generated' | 'search' | undefined {
    if (!node.iconShortlist || node.shortlistIndex === undefined) return undefined;
    const entry = node.iconShortlist[node.shortlistIndex];
    return entry ? getEntryMatchType(entry) : undefined;
}

/**
 * Returns true when the node's icon was resolved via search rather than
 * generation.  Reads from the current shortlist entry's matchType field.
 * Kept as a one-liner alias so existing callers don't break.
 */
export function isIconSearchMatched(node: RecipeNode): boolean {
    return getIconMatchType(node) === 'search';
}

/**
 * Returns the 0-based index of the node's current icon within its shortlist,
 * reading from the explicit shortlistIndex field.  Returns -1 when the
 * shortlist or shortlistIndex is absent.
 */
export function currentShortlistIndex(node: RecipeNode): number {
    if (!node.iconShortlist) return -1;
    if (node.shortlistIndex === undefined) return -1;
    return node.shortlistIndex;
}

/**
 * Returns the next shortlist icon after the node's current icon, or null
 * when the shortlist is exhausted (meaning the caller should fall through to
 * the Firestore reroll path).
 */
export function nextShortlistIcon(node: RecipeNode): IconStats | null {
    const shortlist = node.iconShortlist;
    if (!shortlist || shortlist.length === 0) return null;
    const nextIdx = (node.shortlistIndex ?? 0) + 1;
    if (nextIdx < shortlist.length) return getEntryIcon(shortlist[nextIdx]);
    return null;
}

/**
 * Returns the next index value to be stored on the node after advancing past
 * the current shortlist entry.
 */
export function advanceShortlistIndex(node: RecipeNode): number {
    return (node.shortlistIndex ?? 0) + 1;
}

// ---------------------------------------------------------------------------
// ShortlistEntry helpers — the ONLY place that knows ShortlistEntry internals
// ---------------------------------------------------------------------------

/** Wraps an IconStats + matchType into a ShortlistEntry. */
export function buildShortlistEntry(icon: IconStats, matchType: 'generated' | 'search', matchScore?: number): ShortlistEntry {
    return { icon, matchType, ...(matchScore !== undefined && { matchScore }) };
}

/**
 * Returns the current ShortlistEntry for the node (shortlist[shortlistIndex ?? 0]),
 * or undefined when no shortlist is present.
 */
export function getCurrentEntry(node: RecipeNode): ShortlistEntry | undefined {
    if (!node.iconShortlist || node.iconShortlist.length === 0) return undefined;
    return node.iconShortlist[node.shortlistIndex ?? 0];
}

/** Extracts the IconStats from a ShortlistEntry. */
export function getEntryIcon(entry: ShortlistEntry): IconStats {
    return entry.icon;
}

export function getEntryID(entry: ShortlistEntry): string {
    return entry.icon.id;
}

/** Extracts the matchType from a ShortlistEntry. */
export function getEntryMatchType(entry: ShortlistEntry): 'generated' | 'search' {
    return entry.matchType;
}

export function getEntryHasImpressed(entry: ShortlistEntry): boolean {
    return entry.hasImpressed === true;
}

export function getEntryHasRejected(entry: ShortlistEntry): boolean {
    return entry.hasRejected === true;
}

/** Extracts the matchScore from a ShortlistEntry, or undefined when not set. */
export function getEntryMatchScore(entry: ShortlistEntry): number | undefined {
    return entry.matchScore;
}

/**
 * Cosine similarity between two equal-length vectors. Returns 0 for zero-magnitude inputs.
 */
export function cosineSimilarity(a: number[], b: number[]): number {
    let dot = 0, na = 0, nb = 0;
    for (let i = 0; i < a.length; i++) {
        dot += a[i] * b[i];
        na  += a[i] * a[i];
        nb  += b[i] * b[i];
    }
    if (na === 0 || nb === 0) return 0;
    return dot / (Math.sqrt(na) * Math.sqrt(nb));
}

/**
 * Scores each icon by cosine similarity to `queryVec` using the provided embedding map,
 * then returns a sorted (descending) array of ShortlistEntries with `matchScore` set.
 * Icons missing from `embeddings` receive a score of 0 and sort last.
 */
export function rankIconsByEmbedding(
    icons: IconStats[],
    queryVec: number[],
    embeddings: Map<string, number[]>,
): ShortlistEntry[] {
    const entries = icons.map(icon => {
        const vec = embeddings.get(icon.id);
        const matchScore = vec ? cosineSimilarity(queryVec, vec) : 0;
        return buildShortlistEntry(icon, 'search', matchScore);
    });
    return entries.sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0));
}

/**
 * Prepends entry to existing shortlist (no cap — caller decides slice limits).
 * De-duplicates by icon id so that re-generating an already-present icon
 * doesn't create a duplicate.
 */
export function prependToShortlist(existing: ShortlistEntry[], entry: ShortlistEntry): ShortlistEntry[] {
    const newId = getEntryIcon(entry).id;
    const filtered = existing.filter(e => getEntryIcon(e).id !== newId);
    return [entry, ...filtered];
}

/**
 * Calls `fn` on every node whose standardized ingredient name matches `stdName`.
 * Returns true if at least one node was matched.
 * Use this inside Firestore transactions to avoid repeating the filter boilerplate.
 */
export function mutateNodesByIngredient(
    nodes: any[],
    stdName: string,
    fn: (node: any) => void,
): boolean {
    let matched = false;
    for (const n of nodes) {
        if (!n.visualDescription) continue;
        if (standardizeIngredientName(getNodeIngredientName(n)) !== stdName) continue;
        fn(n);
        matched = true;
    }
    return matched;
}

/**
 * Copies shortlist data (iconShortlist + shortlistIndex) from `source` onto
 * `target`, returning a new object. Use this when reconstructing a node from
 * AI output or JSON edit and you want to preserve the existing icons.
 *
 * Only copies when the source actually has a shortlist.
 */
export function preserveNodeShortlist<T extends RecipeNode>(target: T, source: RecipeNode): T {
    if (!source.iconShortlist || source.iconShortlist.length === 0) return target;
    const targetIds = new Set(target.iconShortlist?.map(e => getEntryIcon(e).id) ?? []);
    const additional = source.iconShortlist.filter(e => !targetIds.has(getEntryIcon(e).id));
    const newShortlist = [...(target.iconShortlist ?? []), ...additional];
    return {
        ...target,
        iconShortlist: newShortlist,
        shortlistIndex: source.shortlistIndex,
    };
}

/**
 * Applies a RecipePatch onto an existing graph, returning a new graph.
 * Icon shortlists on surviving nodes are preserved unchanged.
 */
export function applyPatch(graph: RecipeGraph, patch: RecipePatch): RecipeGraph {
    const removeSet = new Set(patch.removeNodeIds ?? []);
    const removeLaneSet = new Set(patch.removeLaneIds ?? []);

    // Keep surviving nodes, apply updates, clean up dangling inputs refs
    let nodes = graph.nodes
        .filter(n => !removeSet.has(n.id))
        .map(n => {
            const update = patch.updateNodes?.find(u => u.id === n.id);
            const merged = update ? { ...n, ...update } : n;
            // Remove any inputs that pointed to now-removed nodes
            if (merged.inputs && removeSet.size > 0) {
                const cleanInputs = merged.inputs.filter(id => !removeSet.has(id));
                if (cleanInputs.length !== merged.inputs.length) {
                    return { ...merged, inputs: cleanInputs };
                }
            }
            return merged;
        });

    // Append new nodes (mark as pending so icons get resolved)
    if (patch.addNodes) {
        nodes = [...nodes, ...patch.addNodes.map(n => {
            const newNode: RecipeNode = { ...n, status: 'pending' as const };
            // Clean inputs that reference nodes removed in the same patch
            // (e.g. a merged node whose source nodes are in removeNodeIds)
            if (newNode.inputs && removeSet.size > 0) {
                const cleanInputs = newNode.inputs.filter(id => !removeSet.has(id));
                if (cleanInputs.length !== newNode.inputs.length) {
                    return { ...newNode, inputs: cleanInputs };
                }
            }
            return newNode;
        })];
    }

    // Lanes
    let lanes = graph.lanes.filter(l => !removeLaneSet.has(l.id));
    if (patch.addLanes) lanes = [...lanes, ...patch.addLanes];

    return {
        ...graph,
        nodes,
        lanes,
        ...(patch.updateTitle !== undefined && { title: patch.updateTitle }),
    };
}